import React, { useState } from 'react';
import { HandCoins, X, Heart } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';

const TIERS = [
  { key: 'tip_1',  amount: '£1',  label: 'Cheers' },
  { key: 'tip_3',  amount: '£3',  label: 'Solid' },
  { key: 'tip_5',  amount: '£5',  label: 'Big up' },
  { key: 'tip_10', amount: '£10', label: 'Absolute unit' },
];

/**
 * Small button that opens a tip modal + redirects to Stripe Checkout.
 * `recipient` = { user_id, name, username }
 * `postId` (optional) associates the tip with a specific mix.
 * `variant` = 'pill' (post card) | 'primary' (live room) | 'ghost'.
 */
export default function TipButton({ recipient, postId, variant = 'pill' }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(null);

  const startTip = async (lookupKey) => {
    setBusy(lookupKey);
    try {
      const { data } = await api.post('/tips/checkout', {
        lookup_key: lookupKey,
        recipient_user_id: recipient.user_id,
        post_id: postId || null,
        origin_url: window.location.origin,
      });
      if (!data?.checkout_url) throw new Error('No checkout URL');
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not open Stripe checkout');
      setBusy(null);
    }
  };

  const btnCls =
    variant === 'primary' ? 'btn-pill btn-primary flex items-center gap-2 text-sm'
    : variant === 'ghost' ? 'text-[#FFB000] hover:text-white text-xs flex items-center gap-1.5 font-bold'
    : 'text-[#A1A1AA] hover:text-[#FFB000] flex items-center gap-1.5 text-xs font-bold transition-colors';

  return (
    <>
      <button
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        className={btnCls}
        data-testid={`tip-btn-${recipient.user_id}`}
        title={`Tip ${recipient.name || recipient.username}`}
      >
        <HandCoins size={variant === 'primary' ? 16 : 14} />
        <span>Tip</span>
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[80] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setOpen(false)}
          data-testid="tip-modal"
        >
          <div
            className="post-card p-6 max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="text-xs uppercase tracking-[0.3em] font-bold text-[#FFB000]">Tip jar</div>
                <div className="text-white font-heading font-black text-2xl">
                  Show <span className="text-[#FFB000]">{recipient.name || `@${recipient.username}`}</span> some love
                </div>
              </div>
              <button onClick={() => setOpen(false)} className="text-[#A1A1AA] hover:text-white" data-testid="tip-modal-close">
                <X size={18} />
              </button>
            </div>
            <div className="text-[#A1A1AA] text-xs mb-5 leading-relaxed">
              Tips settle straight to the DJ's RAW MIXES payouts. We take 0% — Stripe fees only.
            </div>
            <div className="grid grid-cols-2 gap-2 mb-4">
              {TIERS.map((t) => (
                <button
                  key={t.key}
                  onClick={() => startTip(t.key)}
                  disabled={busy}
                  className={`p-4 rounded-xl border-2 text-left transition-all disabled:opacity-40 ${
                    busy === t.key
                      ? 'border-[#FFB000] bg-[#FFB000]/20'
                      : 'border-white/10 bg-black/40 hover:border-[#FFB000]/60 hover:bg-[#FFB000]/5'
                  }`}
                  data-testid={`tip-tier-${t.key}`}
                >
                  <div className="text-white font-heading font-black text-2xl">{t.amount}</div>
                  <div className="text-[#A1A1AA] text-xs uppercase tracking-widest font-bold">{t.label}</div>
                </button>
              ))}
            </div>
            <div className="text-[10px] text-[#A1A1AA] text-center flex items-center justify-center gap-1.5">
              <Heart size={10} className="text-[#EF4444]" /> Powered by Stripe · Test mode
            </div>
          </div>
        </div>
      )}
    </>
  );
}
